import { Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { ChannelIcon } from '@/components/channels/ChannelIcon';
import { NotFound } from '@/pages/errors/NotFound';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useGoBack } from '@/hooks/useGoBack';
import { useChannels } from '@/hooks/useChannels';
import { useChannelDefinitions } from '@/hooks/useChannelDefinitions';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { buildChannel, IN_SCOPE_CHANNEL_IDS } from '@/lib/channels';

import { MultiItemBasketCard } from './settings/MultiItemBasketCard';
import { NotificationEmailsCard } from './settings/NotificationEmailsCard';
import { SmsNotificationsCard, hasExistingSmsConfig } from './settings/SmsNotificationsCard';
import { EmailTemplatesCard } from './settings/EmailTemplatesCard';

const SETTINGS_CHANNEL_IDS = new Set(['product-reservations', 'local-checkout']);

function SettingsLoading() {
	return (
		<div className="space-y-8">
			<Skeleton className="h-4 w-32" />
			<Skeleton className="h-12 w-72" />
			<Skeleton className="h-48" />
			<Skeleton className="h-64" />
		</div>
	);
}

/**
 * Settings sub-page for the order-channel pair (Product Reservations and
 * Local Checkout). Reachable from the ChannelManage right-sidebar via the
 * "Settings" button. Renders the notification-email card (shared across
 * both channels), the SMS card (Local Checkout only and gated to retailers
 * who already have SMS configured — v1 of NEA-6015), and the per-channel
 * email-template editor.
 *
 * The settings are intentionally shop- and channel-scoped; data lives on
 * the `channels-{stage}` record under `config.{notificationEmails,
 * notificationPhoneNumbers, smsEnabled, multiItemBasket, templates}` and the writes go
 * through the existing `PATCH /api/channels/{shopId}/{type}/update`
 * endpoint (`org:channels:manage`) — no new API surface needed.
 */
export function ChannelSettings() {
	const { id = '' } = useParams();
	const { shop } = useShop();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { definitions, isLoading: defsLoading } = useChannelDefinitions();
	const { billing } = useBillingStatus(shop?.id);
	const { shopPath } = useShopPath();
	const goBack = useGoBack(`/channels/${id}/manage`);

	if (!IN_SCOPE_CHANNEL_IDS.includes(id) || !SETTINGS_CHANNEL_IDS.has(id)) return <NotFound />;
	if (!shop?.id || channelsLoading || defsLoading) return <SettingsLoading />;

	const channel = buildChannel(id, definitions, channels, billing?.entitlements);
	if (!channel) return <NotFound />;
	// Settings only make sense for a live channel — otherwise send the
	// retailer to the detail / setup page where they can switch it on.
	if (!channel.live) return <Navigate to={shopPath(`/channels/${id}`)} replace />;

	const localCheckoutChannel = channels.find((c) => c.type === 'local-checkout');
	const currentChannel = channels.find((c) => c.type === id);
	const notificationEmails = currentChannel?.config?.notificationEmails || [];
	const phoneNumbers = localCheckoutChannel?.config?.notificationPhoneNumbers || [];
	const smsEnabled = Boolean(localCheckoutChannel?.config?.smsEnabled);
	const showSms = id === 'local-checkout' && hasExistingSmsConfig(localCheckoutChannel);

	return (
		<div className="space-y-8">
			<button
				type="button"
				onClick={goBack}
				className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
			>
				<ArrowLeft className="h-3 w-3" /> Back
			</button>

			<header className="flex flex-wrap items-start gap-4">
				<ChannelIcon channelId={channel.id} src={channel.iconUrl} className="h-12 w-12" />
				<div className="min-w-0 flex-1">
					<p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{channel.title}</p>
					<h1 className="mt-1 text-2xl font-extrabold tracking-tight md:text-3xl">Channel settings</h1>
					<p className="mt-1.5 max-w-3xl text-base text-muted-foreground">
						Choose how you want to be notified about new orders and tailor the emails customers receive.
					</p>
				</div>
			</header>

			<div className="space-y-6">
				<MultiItemBasketCard key={id} shopId={shop.id} channelType={id} value={Boolean(currentChannel?.config?.multiItemBasket)} />
				<NotificationEmailsCard shopId={shop.id} channels={channels} value={notificationEmails} />
				{showSms ? (
					<SmsNotificationsCard shopId={shop.id} channels={channels} phoneNumbers={phoneNumbers} smsEnabled={smsEnabled} />
				) : null}
				<EmailTemplatesCard shop={shop} channelType={id} channels={channels} />
			</div>
		</div>
	);
}
